import cn from 'classnames';
import cloneDeep from 'lodash/cloneDeep';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { FormattedDate } from 'react-intl';

import { PrimarySolidButton } from '@bibliocommons/base-buttons';
import { Spinner } from '@bibliocommons/base-icons';

import Heading from 'app/components/shared/Heading';
import BackLink from 'app/components/admin/browse/shared/BackLink';
import ReOrderButtons from 'app/components/admin/browse/shared/ReOrderButtons';
import Section from 'app/components/admin/browse/shared/Section';
import {
  addCategory,
  deleteCategoryFromConfig,
  getSubCategories,
  isUnsavedCategory,
  reOrderSubCategoryIds
} from 'app/helpers/admin/browse/categories';
import { categoryConfigShape } from 'app/shapes/admin/browse/categories';
import CategoryListEditor from './CategoryListEditor';

import './AdminBrowseCategoriesEditPage.scss';

const AdminBrowseCategoriesEditPage = ({
  backLinkLabel,
  backLinkUrl,
  categoryConfig,
  hasUnsavedChanges,
  heading,
  isFetching,
  onChange,
  onSave,
  topLevelCategoryId
}) => {
  const [state, setState] = useState({
    isCategoryExpandedIndex: {}
  });

  const { isCategoryExpandedIndex } = state;

  const handleAddCategory = (parentCategoryId, category) => {
    const newCategoryConfig = addCategory(categoryConfig, parentCategoryId, category);

    onChange(newCategoryConfig);
  };

  const deleteCategory = subCategoryId => {
    const newCategoryConfig = deleteCategoryFromConfig(subCategoryId, categoryConfig);

    onChange(newCategoryConfig);
  };

  const handleReOrder = (parentCategoryId, newSubCategoryIds) => {
    const newCategoryConfig = reOrderSubCategoryIds(parentCategoryId, newSubCategoryIds, categoryConfig);

    onChange(newCategoryConfig);
  };

  const handleCategoryChange = newCategory => {
    const configCopy = cloneDeep(categoryConfig);

    const { categoryId } = newCategory;

    const { categoryById } = configCopy;

    categoryById[categoryId] = newCategory;

    onChange(configCopy);
  };

  const handleCategoryIsExpandedToggle = categoryId => {
    setState(prevState => {
      return {
        ...prevState,
        isCategoryExpandedIndex: {
          ...prevState.isCategoryExpandedIndex,
          [categoryId]: !prevState.isCategoryExpandedIndex[categoryId]
        }
      };
    });
  };

  const { updatedAt = 0, updatedBy = '' } = categoryConfig ?? {};
  const topLevelSubCategoryIds = categoryConfig?.categoryById[topLevelCategoryId]?.subCategoryIds ?? [];
  const topLevelSubCategories = getSubCategories(topLevelCategoryId, categoryConfig);
  const hasNoSubCategories = topLevelSubCategories.length === 0;

  return (
    <div className="cp-admin-browse-categories-edit-page container">
      <BackLink className="back-link" dataKey="back-to-categories" label={backLinkLabel} url={backLinkUrl} />

      <div className="heading-row row">
        <div className="col col-12 col-lg-6">
          <Heading size="large" tagName="h1" variant>
            {heading}
          </Heading>
        </div>
        <div className="col col-12 col-lg-6">
          <div className="save-wrapper">
            {updatedBy && (
              <p className="last-updated">
                Last updated by {updatedBy} on{' '}
                <FormattedDate day="numeric" month="long" value={updatedAt} year="numeric" />
              </p>
            )}
            <PrimarySolidButton
              className="button-save"
              dataKey="AdminBrowseCategoriesEditSaveButton"
              disabled={!hasUnsavedChanges}
              handleClick={onSave}
              isLoading={isFetching}
              type="button"
            >
              Save
            </PrimarySolidButton>
          </div>
        </div>
      </div>

      <section style={{ marginBottom: '40px' }}>
        <Heading className="subheading" size="moderate" tagName="h2">
          Categories
        </Heading>

        {isFetching && <Spinner />}
        {!isFetching && (
          <CategoryListEditor
            categories={topLevelSubCategories}
            categoryColumnHeading="Category"
            deleteCategory={deleteCategory}
            onAddCategory={handleAddCategory}
            onCategoryChange={handleCategoryChange}
            onReOrder={handleReOrder}
            parentCategoryId={topLevelCategoryId}
          />
        )}
      </section>

      <section>
        <Heading
          className={cn('subheading', { 'subheading--no-subcategories': hasNoSubCategories })}
          size="moderate"
          tagName="h2"
        >
          Subcategories
        </Heading>
        {hasNoSubCategories && <p>Add a category above to begin adding subcategories.</p>}
        {topLevelSubCategories.map((subCategory, index) => {
          const { categoryId, title } = subCategory;
          const isUnsaved = isUnsavedCategory(subCategory);
          if (isUnsaved) {
            return null;
          }

          const isExpanded = Boolean(isCategoryExpandedIndex[categoryId]);

          const renderReOrderButtons = () => {
            return (
              <ReOrderButtons
                index={index}
                items={topLevelSubCategoryIds}
                onChange={newOrderedSubCategoryIds => handleReOrder(topLevelCategoryId, newOrderedSubCategoryIds)}
              />
            );
          };

          return (
            <Section
              className="section"
              confirmDeleteModalMessage={`Are you sure you want to delete the '${title}' category?`}
              key={categoryId}
              heading={title}
              isExpanded={isExpanded}
              onDelete={() => deleteCategory(categoryId)}
              onIsExpandedToggle={handleCategoryIsExpandedToggle}
              renderReOrderButtons={renderReOrderButtons}
              sectionId={categoryId}
            >
              <div className="section-body">
                <CategoryListEditor
                  categories={getSubCategories(categoryId, categoryConfig)}
                  categoryColumnHeading="Subcategory"
                  deleteCategory={deleteCategory}
                  onAddCategory={handleAddCategory}
                  onCategoryChange={handleCategoryChange}
                  onReOrder={handleReOrder}
                  parentCategoryId={categoryId}
                />
              </div>
            </Section>
          );
        })}
      </section>
    </div>
  );
};

AdminBrowseCategoriesEditPage.propTypes = {
  backLinkLabel: PropTypes.string.isRequired,
  backLinkUrl: PropTypes.string.isRequired,
  categoryConfig: categoryConfigShape,
  hasUnsavedChanges: PropTypes.bool.isRequired,
  heading: PropTypes.string.isRequired,
  isFetching: PropTypes.bool.isRequired,
  onChange: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  topLevelCategoryId: PropTypes.string.isRequired
};

export default AdminBrowseCategoriesEditPage;
